import { useTranslation } from 'react-i18next';
import { MAP_FONTS_TO_CHALK } from 'utils/constants';

import React, { useEffect, useState } from 'react';

import { Button, TextField } from '@thinkific/chalk/react-components';
import { ChalkThemeProvider } from '@thinkific/chalk/react-providers';

import { useCommunityContext } from 'components/CommunityContext';
import type { Editor } from 'components/new/Posts/hooks/useEditor/useEditor';

import * as css from './LinkForm.css';

interface LinkFormProps {
  editor: Editor;
  onChange: () => void;
}

interface linkAttributes {
  text: string;
  url: string;
}

interface formErrors {
  text?: string;
  url?: string;
}

const LinkForm = ({ editor, onChange }: LinkFormProps) => {
  const [link, setLink] = useState<linkAttributes>({
    text: '',
    url: '',
  });

  const [errors, setErrors] = useState<formErrors>({});
  const { community } = useCommunityContext();

  const { t } = useTranslation();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setLink((prevLink) => ({ ...prevLink, [name]: value }));
  };

  const validateForm = () => {
    const validationErrors: formErrors = {};

    if (!link.text.trim()) {
      validationErrors.text = t(
        'community.documentEditor.customLink.invalidText',
        'Text cannot be empty'
      );
    }

    if (!/^https?:\/\//.test(link.url)) {
      validationErrors.url = t(
        'community.documentEditor.menuBar.invalidUrl',
        'Please use a valid URL'
      );
    }

    return validationErrors;
  };

  const applyLink = (e: React.KeyboardEvent | React.MouseEvent) => {
    e.preventDefault();
    if (!editor) return;
    const newErrors = validateForm();
    setErrors(newErrors);

    if (Object.keys(newErrors).length !== 0) return;

    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .command(({ tr, dispatch }) => {
        if (!dispatch) return true;

        const { from, to } = tr.selection;

        const displayText = link.text || link.url || '';

        tr.insertText(displayText, from, to);
        tr.addMark(
          from,
          from + displayText.length,
          editor.schema.marks.link.create({
            href: link.url,
          })
        );

        return true;
      })
      .unsetMark('link')
      .insertContent(' ')
      .focus()
      .run();
    onChange?.();
  };

  const removeLink = () => {
    if (!editor) return;
    editor.chain().unsetMark('link', { extendEmptyMarkRange: true }).run();
    onChange?.();
  };

  useEffect(() => {
    if (!editor) return;
    const { from, to, empty } = editor.state.selection;

    const linkNode = editor.state.doc.nodeAt(from);
    if (linkNode?.marks?.[0]?.type?.name === 'link') {
      setLink({
        text: linkNode.textContent,
        url: linkNode.marks[0].attrs.href,
      });
    } else {
      setLink({
        text: empty ? '' : editor.state.doc.textBetween(from, to, ' '),
        url: editor.getAttributes('link').href || '',
      });
    }
  }, [editor]);

  return (
    <ChalkThemeProvider
      bodyFont={MAP_FONTS_TO_CHALK[community.font]}
      primaryColor={community.primaryColor}
      titleFont={MAP_FONTS_TO_CHALK[community.font]}
    >
      <div className={css.popoverContainer}>
        <TextField
          error={errors.text && errors.text}
          label={t('community.documentEditor.customLink.linkTextLabel', 'Text')}
          name="text"
          type="text"
          value={link.text}
          onChange={handleChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              applyLink(e);
            }
          }}
        />

        <TextField
          error={errors.url}
          label={t('community.documentEditor.customLink.linkUrlLabel', 'URL')}
          name="url"
          placeholder="https://www.example.com"
          type="url"
          value={link.url}
          onChange={handleChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              applyLink(e);
            }
          }}
        />

        <div className={css.buttonContainer}>
          <Button disabled={!editor?.isActive('link')} emphasis="low" onClick={removeLink}>
            {t('community.documentEditor.customLink.removeLink', 'Remove')}
          </Button>

          <Button variant="primary" onClick={applyLink}>
            {t('community.documentEditor.customLink.applyLink', 'Apply')}
          </Button>
        </div>
      </div>
    </ChalkThemeProvider>
  );
};

export default LinkForm;
